/**
 * H-001 — Turnstile client integration (§7.2, §13).
 *
 * Managed widget, explicit rendering, `execution: "execute"` and
 * `appearance: "interaction-only"`: the widget occupies no screen space until
 * Cloudflare actually needs the participant to do something, and no token is
 * requested until a send is attempted. A token fetched earlier would be five
 * minutes closer to expiry by the time the network came back.
 *
 * The token is returned to the caller and nothing else: it is never stored,
 * logged, or written to IndexedDB (AGENTS.md invariant 3).
 */

/** What the submission service needs. Kept tiny so tests can fake it. */
export interface TurnstileExecutor {
  /** Resolve with a *fresh* token, running the challenge if one is required. */
  execute(): Promise<string>;
  /** Discard the current token so the next execute() starts a new challenge. */
  reset(): void;
}

export interface TurnstileRenderOptions {
  sitekey: string;
  action?: string;
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
  callback?: (token: string) => void;
  'error-callback'?: (code?: string) => void;
  'expired-callback'?: () => void;
  'timeout-callback'?: () => void;
}

/** The subset of `window.turnstile` this app uses. */
export interface TurnstileApi {
  render(container: HTMLElement, options: TurnstileRenderOptions): string | undefined;
  execute(widget: string | HTMLElement, options?: TurnstileRenderOptions): void;
  reset(widget?: string | HTMLElement): void;
  remove(widget: string | HTMLElement): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const SCRIPT_ID = 'cf-turnstile-script';

/** §7.4 — a challenge the participant never finishes must not hang the retry engine. */
const DEFAULT_EXECUTE_TIMEOUT_MS = 60_000;

export class TurnstileError extends Error {
  constructor(readonly reason: 'unavailable' | 'failed' | 'timeout') {
    super(`turnstile_${reason}`);
    this.name = 'TurnstileError';
  }
}

/** Load `api.js` once per document and resolve with the global it installs. */
export function loadTurnstileApi(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);

  return new Promise((resolve, reject) => {
    const fail = () => {
      reject(new TurnstileError('unavailable'));
    };
    const settle = () => {
      if (window.turnstile) resolve(window.turnstile);
      else fail();
    };

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) {
      existing.addEventListener('load', settle, { once: true });
      existing.addEventListener('error', fail, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener('load', settle, { once: true });
    script.addEventListener('error', fail, { once: true });
    document.head.append(script);
  });
}

export interface TurnstileClientOptions {
  siteKey: string;
  /** Must match the API's `TURNSTILE_ACTION` (§7.5). */
  action?: string;
  /** Where the interactive challenge is rendered — inside the form (§13). */
  container: () => HTMLElement | null;
  loadApi?: () => Promise<TurnstileApi>;
  executeTimeoutMs?: number;
}

/**
 * One widget, executed on demand.
 *
 * Every `execute()` resets first, so a retry can never reuse the token that
 * just failed Siteverify — single-use tokens are rejected on replay (§7.4).
 */
export class TurnstileClient implements TurnstileExecutor {
  readonly #options: TurnstileClientOptions;
  readonly #loadApi: () => Promise<TurnstileApi>;
  #api: TurnstileApi | null = null;
  #widgetId: string | null = null;
  #pending: {
    resolve: (token: string) => void;
    reject: (error: TurnstileError) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;

  constructor(options: TurnstileClientOptions) {
    this.#options = options;
    this.#loadApi = options.loadApi ?? loadTurnstileApi;
  }

  async #ensureWidget(): Promise<{ api: TurnstileApi; widgetId: string }> {
    this.#api ??= await this.#loadApi();
    const api = this.#api;

    if (this.#widgetId === null) {
      const container = this.#options.container();
      if (!container) throw new TurnstileError('unavailable');

      const widgetId = api.render(container, {
        sitekey: this.#options.siteKey,
        action: this.#options.action ?? 'event_checkin',
        execution: 'execute',
        appearance: 'interaction-only',
        callback: (token) => {
          this.#settle({ token });
        },
        'error-callback': () => {
          this.#settle({ error: new TurnstileError('failed') });
        },
        'expired-callback': () => {
          this.#settle({ error: new TurnstileError('failed') });
        },
        'timeout-callback': () => {
          this.#settle({ error: new TurnstileError('timeout') });
        },
      });

      if (widgetId === undefined) throw new TurnstileError('unavailable');
      this.#widgetId = widgetId;
    }

    return { api, widgetId: this.#widgetId };
  }

  #settle(outcome: { token: string } | { error: TurnstileError }): void {
    const pending = this.#pending;
    if (!pending) return;
    this.#pending = null;
    clearTimeout(pending.timer);
    if ('token' in outcome) pending.resolve(outcome.token);
    else pending.reject(outcome.error);
  }

  async execute(): Promise<string> {
    const { api, widgetId } = await this.#ensureWidget();

    // A second execute() while one is in flight would leave the first promise
    // dangling; §16 also forbids stacking challenges in a tight loop.
    if (this.#pending) throw new TurnstileError('failed');

    const token = new Promise<string>((resolve, reject) => {
      this.#pending = {
        resolve,
        reject,
        timer: setTimeout(() => {
          this.#settle({ error: new TurnstileError('timeout') });
        }, this.#options.executeTimeoutMs ?? DEFAULT_EXECUTE_TIMEOUT_MS),
      };
    });

    api.reset(widgetId);
    api.execute(widgetId);
    return token;
  }

  reset(): void {
    this.#settle({ error: new TurnstileError('failed') });
    if (this.#api && this.#widgetId !== null) this.#api.reset(this.#widgetId);
  }

  destroy(): void {
    this.#settle({ error: new TurnstileError('failed') });
    if (this.#api && this.#widgetId !== null) this.#api.remove(this.#widgetId);
    this.#widgetId = null;
  }
}

/**
 * Cloudflare's documented always-passes test key.
 *
 * Used only when no site key is configured, so `pnpm dev` works without a
 * Turnstile account. A production build without `VITE_TURNSTILE_SITE_KEY` is
 * a misconfiguration, not a convenience, and fails loudly at startup instead
 * of shipping a widget that verifies nothing.
 */
export const TURNSTILE_TEST_SITE_KEY = '1x00000000000000000000AA';

export function resolveSiteKey(
  configured: string | undefined,
  isProduction: boolean,
): string | null {
  const siteKey = configured?.trim();
  if (siteKey) return siteKey;
  return isProduction ? null : TURNSTILE_TEST_SITE_KEY;
}
